//CouponManagement.vue
<template lang="pug">
div.coupon-management
  v-container
    // Header con título y botones
    v-row.mb-4
      v-col(cols="12" sm="6")
        h1.text-h4.primary--text.font-weight-bold Gestión de Cupones de Descuento
      v-col(cols="12" sm="6" class="d-flex justify-end align-center")
        v-btn(color="primary" @click="openCreateDialog") 
          v-icon(left) mdi-plus
          | Crear Cupón

    // Alerta de error
    v-alert(
      v-if="error"
      type="error"
      dismissible
      @input="clearError"
      dense
    ) {{ error }}

    // Filtros
    v-card.mb-4(outlined)
      v-card-title Filtros
      v-card-text
        v-row
          v-col(cols="12" sm="4")
            v-select(
              v-model="filters.status"
              :items="statusOptions"
              label="Estado"
              outlined
              dense
              hide-details
              @change="fetchCoupons"
            )
          v-col(cols="12" sm="4")
            v-select(
              v-model="filters.type"
              :items="typeOptions"
              label="Tipo"
              outlined
              dense
              hide-details
              @change="fetchCoupons"
            )
          v-col(cols="12" sm="4")
            v-text-field(
              v-model="filters.search"
              label="Buscar"
              outlined
              dense
              hide-details
              prepend-inner-icon="mdi-magnify"
              clearable
              @input="debouncedSearch"
            )

    // Estadísticas
    v-row.mb-4(v-if="stats")
      v-col(cols="12" sm="4")
        v-card(outlined color="primary" dark)
          v-card-text.text-center
            div.text-h5 {{ stats.activeCount }}
            div.text-subtitle-2 Cupones Activos
      v-col(cols="12" sm="4")
        v-card(outlined)
          v-card-text.text-center
            div.text-h5 {{ getTotalUsageCount }}
            div.text-subtitle-2 Usos Totales
      v-col(cols="12" sm="4")
        v-card(outlined)
          v-card-text.text-center
            div.text-h5 {{ stats.expiredCount }}
            div.text-subtitle-2 Cupones Vencidos

    // Tabla de cupones
    v-card(outlined)
      v-data-table(
        :headers="headers"
        :items="coupons"
        :loading="loading"
        :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
        sort-by="createdAt"
        :sort-desc="true"
        :search="filters.search"
      )
        template(v-slot:item.code="{ item }")
          div.d-flex.align-center
            v-tooltip(bottom)
              template(v-slot:activator="{ on, attrs }")
                div.font-weight-medium.cursor-pointer(
                  v-bind="attrs"
                  v-on="on"
                  @click="copyToClipboard(item.code, item._id)"
                  :class="{'success--text': copied && copiedId === item._id}"
                )
                  | {{ item.code }}
                  v-icon.ml-1(small) {{ copied && copiedId === item._id ? 'mdi-check' : 'mdi-content-copy' }}
              | Click para copiar al portapapeles
          div.text-caption.grey--text {{ item.title }}

        template(v-slot:item.discountValue="{ item }")
          v-chip(:color="getDiscountColor(item)" text-color="white" small)
            template(v-if="item.discountType === 'PERCENTAGE'") {{ item.discountValue }}%
            template(v-else-if="item.discountType === 'FIXED_AMOUNT'") ${{ item.discountValue }}
            template(v-else) {{ getDiscountTypeLabel(item.discountType) }}
        
        template(v-slot:item.type="{ item }")
          v-chip(small outlined :color="getCouponTypeColor(item.type)")
            | {{ getCouponTypeLabel(item.type) }}
        
        template(v-slot:item.expiresAt="{ item }")
          div {{ formatDate(item.expiresAt) }}
          div.text-caption(v-if="isExpired(item)")
            v-icon(small color="error") mdi-alert-circle
            span.error--text.ml-1 Vencido
            
        template(v-slot:item.usedCount="{ item }")
          div {{ item.usedCount }} / {{ item.maxUses || '∞' }}
          
        template(v-slot:item.active="{ item }")
          v-chip(
            small
            :color="item.active ? 'success' : 'grey'"
            text-color="white"
          ) {{ item.active ? 'Activo' : 'Inactivo' }}

        template(v-slot:item.actions="{ item }")
          v-btn(icon small @click="viewCoupon(item)")
            v-icon(small) mdi-eye
          v-btn(
            icon
            small
            @click="editCoupon(item)"
            :disabled="isExpired(item)"
          )
            v-icon(small) mdi-pencil
          v-btn(
            icon
            small
            color="error"
            @click="confirmDeactivate(item)"
            v-if="item.active"
          )
            v-icon(small) mdi-close-circle

    // Diálogo de creación/edición
    coupon-form-dialog(
      v-if="showFormDialog"
      v-model="showFormDialog"
      :coupon="selectedCoupon"
      :editing="editing"
      @save="handleSave"
    )

    // Diálogo de visualización
    coupon-view-dialog(
      v-if="showViewDialog"
      v-model="showViewDialog"
      :coupon="selectedCoupon"
    )
    
    // Diálogo de confirmación
    v-dialog(v-model="showConfirmDialog" max-width="400")
      v-card
        v-card-title ¿Desactivar este cupón?
        v-card-text
          p ¿Estás seguro de que querés desactivar el cupón "{{ selectedCoupon ? selectedCoupon.code : '' }}"?
          p.text-caption.grey--text Una vez desactivado, no podrá ser utilizado por ningún cliente.
        v-card-actions
          v-spacer
          v-btn(text @click="showConfirmDialog = false") Cancelar
          v-btn(color="error" @click="deactivateCoupon") Desactivar
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import CouponFormDialog from './dialogs/CouponFormDialog.vue'
import CouponViewDialog from './dialogs/CouponViewDialog.vue'
import debounce from 'lodash/debounce'

export default {
  name: 'CouponManagement',

  components: {
    CouponFormDialog,
    CouponViewDialog
  },

  data: () => ({
    // Variables for clipboard functionality
    copied: false,
    copiedId: null,
    copyTimer: null,
    
    filters: {
      status: 'all',
      type: '',
      search: ''
    },
    headers: [
      { text: 'Código/Título', value: 'code', sortable: true },
      { text: 'Descuento', value: 'discountValue', sortable: true },
      { text: 'Tipo', value: 'type', sortable: true },
      { text: 'Vencimiento', value: 'expiresAt', sortable: true },
      { text: 'Usos', value: 'usedCount', sortable: true },
      { text: 'Estado', value: 'active', sortable: true },
      { text: 'Acciones', value: 'actions', sortable: false, align: 'center' }
    ],
    showFormDialog: false,
    showViewDialog: false,
    showConfirmDialog: false,
    selectedCoupon: null,
    editing: false,
    statusOptions: [
      { text: 'Todos', value: 'all' },
      { text: 'Activos', value: 'active' },
      { text: 'Inactivos', value: 'inactive' },
      { text: 'Vencidos', value: 'expired' }
    ],
    typeOptions: [
      { text: 'Todos los tipos', value: '' },
      { text: 'Estándar', value: 'STANDARD' },
      { text: 'Primera compra', value: 'FIRST_ORDER' },
      { text: 'Fidelidad', value: 'LOYALTY' },
      { text: 'Cumpleaños', value: 'BIRTHDAY' },
      { text: 'Referido', value: 'REFERRAL' },
      { text: 'Recuperación', value: 'RECOVERY' },
      { text: 'Compensación', value: 'COMPENSATION' },
      { text: 'Temporada', value: 'SEASONAL' }
    ]
  }),

  computed: {
    ...mapState('coupons', ['list', 'loading', 'error', 'stats']),
    ...mapGetters('coupons', ['activeCoupons', 'expiredCoupons']),
    
    coupons() {
      return this.list
    },
    
    getTotalUsageCount() {
      if (!this.stats || !this.stats.byType) return 0
      
      return this.stats.byType.reduce((total, item) => {
        return total + (item.usedCount || 0)
      }, 0)
    }
  },

  created() {
    this.fetchCoupons()
    this.fetchStats()
    // Configurar búsqueda con debounce
    this.debouncedSearch = debounce(() => {
      this.fetchCoupons()
    }, 300)
  },

  methods: {
    /**
     * Copy coupon code to clipboard
     * @param {string} code - Coupon code to copy
     * @param {string} id - Coupon ID for visual feedback
     */
    copyToClipboard(code, id) {
      // Clear any previous timer
      if (this.copyTimer) {
        clearTimeout(this.copyTimer);
      }
      
      // Try to copy to clipboard
      navigator.clipboard.writeText(code)
        .then(() => {
          this.copied = true;
          this.copiedId = id;
          
          // Show notification
          this.$store.dispatch('notifications/show', {
            text: `¡Código ${code} copiado al portapapeles!`,
            color: 'success'
          });
          
          // Reset state after 2 seconds
          this.copyTimer = setTimeout(() => {
            this.copied = false;
            this.copiedId = null;
          }, 2000);
        })
        .catch(error => {
          console.error('Error copying to clipboard:', error);
          this.$store.dispatch('notifications/show', {
            text: 'No se pudo copiar al portapapeles',
            color: 'error'
          });
        });
    },
    
    ...mapActions('coupons', [
      'fetch',
      'fetchStats',
      'create',
      'update',
      'deactivate',
      'clearError'
    ]),
    
    fetchCoupons() {
      this.fetch(this.filters)
    },
    
    openCreateDialog() {
      this.selectedCoupon = {
        title: '',
        description: '',
        type: 'STANDARD',
        discountType: 'PERCENTAGE',
        discountValue: 10,
        minOrderAmount: 0,
        maxUses: 100,
        startDate: new Date().toISOString().substr(0, 10),
        expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().substr(0, 10),
        active: true
      }
      this.editing = false
      this.showFormDialog = true
    },
    
    editCoupon(coupon) {
      this.selectedCoupon = { ...coupon }
      this.editing = true
      this.showFormDialog = true
    },
    
    viewCoupon(coupon) {
      this.selectedCoupon = { ...coupon }
      this.showViewDialog = true
    },
    
    async handleSave(couponData) {
      try {
        if (this.editing) {
          await this.update({
            couponId: this.selectedCoupon._id,
            couponData
          })
        } else {
          await this.create(couponData)
        }
        this.showFormDialog = false
        this.fetchCoupons()
        this.fetchStats()
      } catch (error) {
        console.error('Error saving coupon:', error)
      }
    },
    
    confirmDeactivate(coupon) {
      this.selectedCoupon = coupon
      this.showConfirmDialog = true
    },
    
    async deactivateCoupon() {
      try {
        await this.deactivate(this.selectedCoupon._id)
        this.showConfirmDialog = false
        this.fetchCoupons()
        this.fetchStats()
      } catch (error) {
        console.error('Error deactivating coupon:', error)
      }
    },
    
    formatDate(dateString) {
      const date = new Date(dateString)
      return new Intl.DateTimeFormat('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(date)
    },
    
    isExpired(coupon) {
      return new Date(coupon.expiresAt) < new Date()
    },
    
    getDiscountColor(coupon) {
      switch (coupon.discountType) {
        case 'PERCENTAGE':
          return 'primary'
        case 'FIXED_AMOUNT':
          return 'success'
        case 'FREE_SHIPPING':
          return 'purple'
        case 'FREE_ITEM':
          return 'orange'
        default:
          return 'grey'
      }
    },
    
    getDiscountTypeLabel(type) {
      const labels = {
        'PERCENTAGE': 'Porcentaje',
        'FIXED_AMOUNT': 'Monto fijo',
        'FREE_SHIPPING': 'Envío gratis',
        'FREE_ITEM': 'Item gratis'
      }
      return labels[type] || type
    },
    
    getCouponTypeLabel(type) {
      const labels = {
        'STANDARD': 'Estándar',
        'FIRST_ORDER': 'Primera compra',
        'LOYALTY': 'Fidelidad',
        'BIRTHDAY': 'Cumpleaños',
        'REFERRAL': 'Referido',
        'RECOVERY': 'Recuperación',
        'COMPENSATION': 'Compensación',
        'SEASONAL': 'Temporada'
      }
      return labels[type] || type
    },
    
    getCouponTypeColor(type) {
      const colors = {
        'STANDARD': 'grey',
        'FIRST_ORDER': 'blue',
        'LOYALTY': 'deep-purple',
        'BIRTHDAY': 'pink',
        'REFERRAL': 'teal',
        'RECOVERY': 'amber',
        'COMPENSATION': 'red',
        'SEASONAL': 'green'
      }
      return colors[type] || 'grey'
    }
  }
}
</script>

<style scoped>
.coupon-management {
  padding: 20px 0;
}
</style>
